// Foreground FCM handler.
//
// FCM's default behaviour: when the app is BACKGROUND, the service worker
// (`public/firebase-messaging-sw.js`) shows the system notification. When
// the app is FOREGROUND, onMessage fires but NO notification is drawn —
// we have to do that ourselves here, plus invalidate queries so the UI
// updates without a refresh.
//
// Called once from AppShell after `useFcmAutoRegister`.

import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useCurrentClubStore } from '@/core/auth/current-club-store'
import { onFcmForegroundMessage } from './fcm'
import { resolveFcmDeepLink, type FcmDataPayload } from './deep-link'
import { logger } from '@/core/logger'

export function useFcmForeground(): void {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const clubId = useCurrentClubStore((s) => s.clubId)

  useEffect(() => {
    if (!clubId) return
    if (typeof window === 'undefined' || !('Notification' in window)) return

    const unsub = onFcmForegroundMessage((payload) => {
      logger.i('FCM foreground', payload)

      // Surface-agnostic: invalidate the common read paths so whichever
      // screen the operator is on picks up the change. Cheap — TanStack
      // Query dedupes + only refetches mounted observers.
      qc.invalidateQueries({ queryKey: ['bookings', clubId] })
      qc.invalidateQueries({ queryKey: ['activity-log', clubId] })


      const title = payload.notification?.title || 'La aplicación'
      const body = payload.notification?.body || ''
      const data = (payload.data ?? {}) as FcmDataPayload
      const deepPath = resolveFcmDeepLink(data)

      // In-app toast for foreground — clickable to jump to the deep link.
      // Fits the same pattern as our other sonner toasts and doesn't rely
      // on OS notification permission quirks.
      toast(title, {
        description: body || undefined,
        duration: 6000,
        ...(deepPath
          ? {
              action: {
                label: 'Abrir',
                onClick: () => navigate(deepPath),
              },
            }
          : {}),
      })

      // Best-effort system notification too (covers the case where the
      // tab is open but unfocused — the OS banner is the affordance).
      // Click handler navigates via the same resolver before focusing.
      if (Notification.permission === 'granted' && payload.notification && !document.hasFocus()) {
        try {
          const n = new Notification(title, {
            body,
            icon: '/icons/Icon-192.png',
            badge: '/icons/Icon-192.png',
            tag: 'clubmanager-foreground',
          })
          n.onclick = () => {
            window.focus()
            if (deepPath) navigate(deepPath)
            n.close()
          }
        } catch (err) {
          logger.w('foreground notification failed', err)
        }
      }
    })

    return () => {
      unsub()
    }
  }, [clubId, qc, navigate])
}
